/**
 * Zeitbalken: Abschnitte nebeneinander, Breite nach Dauer. Aktive Zeit ist
 * kräftig gefärbt, Wartezeit blass. Darunter eine Legende mit Minuten.
 */
import { formatMinutes, segmentTotals } from '@cookbook/domain';
import type { TimeSegment } from '@cookbook/domain';
import { texts } from '../texts/de.ts';

/** Eigenschaften des Zeitbalkens. */
export interface TimeBarProps {
  readonly segments: readonly TimeSegment[];
  readonly title: string;
}

/** Zeitbalken mit Legende. */
export function TimeBar({ segments, title }: TimeBarProps) {
  if (segments.length === 0) {
    return null;
  }
  const totals = segmentTotals(segments);
  const summary = `${formatMinutes(totals.active)} ${texts.scale.activeLegend}, ${formatMinutes(totals.passive)} ${texts.scale.passiveLegend}`;
  return (
    <figure className="text-xs">
      <figcaption className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium text-content-muted">{title}</span>
        <span className="text-content-muted">{summary}</span>
      </figcaption>
      <div
        className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full"
        role="img"
        aria-label={`${title}: ${summary}`}
      >
        {segments.map((segment) => (
          <div
            key={segment.key}
            title={`${segment.label}: ${formatMinutes(segment.minutes)}`}
            className={segment.active ? 'bg-accent' : 'bg-content-muted/30'}
            style={{ flexGrow: segment.minutes, flexBasis: 0, minWidth: '6px' }}
          />
        ))}
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
        {segments.map((segment) => (
          <li key={segment.key} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className={`inline-block h-2.5 w-2.5 rounded-full ${segment.active ? 'bg-accent' : 'bg-content-muted/30'}`}
            />
            <span>{segment.label}</span>
            <span className="text-content-muted">{formatMinutes(segment.minutes)}</span>
          </li>
        ))}
      </ul>
    </figure>
  );
}
